// src/components/DeleteCustomerModal.js

import React, { useState, useCallback } from 'react';
import {
  Modal,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  getFirestore,
  doc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  updateDoc,
} from 'firebase/firestore';
import CustomerSelectionTable from './CustomerSelectionTable';
import { handleDataTransfer, deleteCustomerWithDebt } from './TransferCustomerModal';
import { auth } from './firebaseConfig';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

const DeleteCustomerModal = ({
  isOpen,
  onClose,
  selectedCustomer,
  setAlertOpen,
  setError,
  setSuccessMessage,
  setSelectedCustomer,
  onTransfer
}) => {
  const [showTransferOption, setShowTransferOption] = useState(false);
  const [selectedTargetCustomer, setSelectedTargetCustomer] = useState(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);

  // Diğer müşterileri getir
  const fetchAvailableCustomers = useCallback(async () => {
    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      const customerQuery = query(
        collection(db, `users/${userEmail}/customerList`),
        where('Onay', '==', 'Onaylandı'),
        where('__name__', '!=', selectedCustomer.id)
      );
      const snapshot = await getDocs(customerQuery);
      const customers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableCustomers(customers);
    } catch (error) {
      console.error('Müşteriler getirilirken hata oluştu:', error);
      setError('Müşteriler getirilirken hata oluştu.');
      setAlertOpen(true);
    }
  }, [selectedCustomer, setError, setAlertOpen]);

  const handleTransferAndDelete = async () => {
    if (!selectedTargetCustomer) {
      setError('Lütfen hedef müşteriyi seçin.');
      setAlertOpen(true);
      return;
    }

    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // Şantiyeleri hedef müşteriye transfer et
      const shantiyeQuery = query(
        collection(db, `users/${userEmail}/customerList`),
        where('parentId', '==', selectedCustomer.id)
      );
      const shantiyeSnapshot = await getDocs(shantiyeQuery);
      
      // Transfer işlemleri...
      
      // Ana müşteriyi sil
      await deleteDoc(doc(db, `users/${userEmail}/customerList`, selectedCustomer.id));
      
      setSuccessMessage('Müşteri ve şantiyeleri başarıyla transfer edildi ve silindi.');
      setAlertOpen(true);
      onClose();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Transfer ve silme işleminde hata oluştu:', error);
      setError('Transfer ve silme işleminde hata oluştu.');
      setAlertOpen(true);
    }
  };

  const handleDelete = async () => {
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // Müşteriyi debt subcollection'ı ile birlikte sil
      await deleteCustomerWithDebt(userEmail, selectedCustomer.id);

      // Setup progress kontrolü - Kalan müşteri sayısını kontrol et
      const db = getFirestore();
      const customerQuery = query(
        collection(db, `users/${userEmail}/customerList`),
        where('Onay', '==', 'Onaylandı')
      );
      const customerSnapshot = await getDocs(customerQuery);
      const remainingCustomerCount = customerSnapshot.size;

      // Setup progress'i güncelle
      const userDocRef = doc(db, 'users', userEmail);
      await updateDoc(userDocRef, {
        'setupProgress.customers': remainingCustomerCount > 0
      });

      console.log('✅ Müşteri silindi, setup progress güncellendi:', {
        remainingCustomers: remainingCustomerCount,
        setupCompleted: remainingCustomerCount > 0
      });

      setSuccessMessage('Müşteri ve tüm borç kayıtları başarıyla silindi.');
      setAlertOpen(true);
      onClose();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Müşteri silinirken hata oluştu:', error);
      setError('Müşteri silinirken hata oluştu.');
      setAlertOpen(true);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalBox>
        <Typography variant="h6" gutterBottom>
          Müşteriyi Sil
        </Typography>
        {selectedCustomer && (
          <Box mb={2}>
            <Typography>
              <strong>Müşteri Adı:</strong> {selectedCustomer['Müşteri Adı']}
            </Typography>
            <Typography>
              <strong>Telefon:</strong> {selectedCustomer['Telefon']}
            </Typography>
            <Typography>
              <strong>E-posta:</strong> {selectedCustomer['E-posta']}
            </Typography>
            <Typography>
              <strong>Cari Kodu:</strong> {selectedCustomer.cariCode}
            </Typography>
          </Box>
        )}

        <Typography color="error" mb={2}>
          Bu müşteriyi sildiğinizde, ilgili puantaj verileri kaybolacaktır.
        </Typography>

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose}>
            İptal
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onClose();
              onTransfer(selectedCustomer);
            }}
          >
            Verileri Taşı
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
          >
            Sil
          </Button>
        </Box>
      </ModalBox>
    </Modal>
  );
};

export default DeleteCustomerModal;
